<template>
  <div>
    <Modal
      :open="open"
      :border="false"
      width="extra-large"
      :classes="{ modalContent: 'max-md:!h-auto !max-h-[calc(100vh-200px)] !overflow-y-auto' }"
      :backdrop-close="false"
      @on-close="closeModal"
    >
      <template #body>
        <div>
          <h1 class="text-lg font-medium text-app-gray-900 border-b border-app-gray-200 pb-2 mb-2">Change Plan</h1>
          <div class="grid sm:grid-cols-2 gap-4 !mb-2">
            <div v-if="creditCard">
              <CreditCardDisplay :credit-card="creditCard" :selected-team-id="selectedTeamId" @resetPaymentError="resetPaymentError" />
              <div v-if="Boolean(paymentError)" class="text-app-red-500 text-right text-sm mt-3">{{ paymentError }}</div>
            </div>

            <div v-else class="border border-app-gray-200 rounded-lg p-5">
              <div v-if="!stripeElements && !stripeLoadError" class="flex item-center justify-center h-full mt-14">
                <Loader />
              </div>
              <div v-else-if="stripeLoadError" class="text-app-red-500 text-sm mt-14 text-center">Error: {{ stripeLoadError }}</div>
              <div v-else>
                <h2 class="text-2xl font-bold text-app-gray-900 mb-3">Enter Billing Details</h2>
                <PaymentElement :stripe-elements="stripeElements" />
                <div v-if="Boolean(paymentError)" class="text-app-red-500 text-right text-sm mt-3">{{ paymentError }}</div>
              </div>
            </div>
            <div class="border border-app-gray-200 rounded-lg p-5 !h-fit">
              <h2 class="mb-5 flex items-center justify-between">
                <span class="text-2xl font-bold text-app-gray-900 flex items-center gap-2">
                  Web3Auth
                  <Badge variant="info" class="w-max uppercase">{{ plan.name }}</Badge>
                </span>
                <span class="text-lg font-semibold text-app-gray-500">{{ getPlanPrice }}</span>
              </h2>
              <p class="text-base font-normal text-app-gray-500 border-b border-app-gray-200 pb-4">
                {{ `You’ll be charged ${getPlanPrice} ${isYearlySelected ? "yearly" : "monthly"} until you cancel or change your subscription.` }}
              </p>
              <div class="mt-4">
                <TextField
                  :model-value="couponCode"
                  label="Enter coupon"
                  helper-text="If you have a coupon code, enter it here. It will be applied when you change plan."
                  placeholder="e.g. W3AOFFER"
                  maxlength="64"
                  @update:model-value="$emit('update:couponCode', $event)"
                />
              </div>
              <div class="mt-4">
                <Button block :loading="confirmingSubscription" @on-click="confirmSubscription">Change Plan</Button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script lang="ts">
import { StripeElements } from "@stripe/stripe-js";
import { Badge } from "@toruslabs/vue-components/Badge";
import { Loader } from "@toruslabs/vue-components/Loader";
import { Modal } from "@toruslabs/vue-components/Modal";
import { defineComponent } from "vue";

import { Button } from "@/components/Button";
import { TextField } from "@/components/TextField";

import CreditCardDisplay from "../CreditCardDisplay";
import PaymentElement from "../PaymentElement/PaymentElement.vue";

export default defineComponent({
  name: "ChangePlanModal",
  components: {
    Badge,
    Button,
    Loader,
    Modal,
    TextField,
    PaymentElement,
    CreditCardDisplay,
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    plan: {
      type: Object,
      required: true,
    },
    planName: {
      type: String,
      required: true,
    },
    isYearlySelected: {
      type: Boolean,
      default: false,
    },
    stripeElements: {
      type: Object as () => StripeElements | null,
      default: null,
    },
    stripeLoadError: {
      type: String,
      default: "",
    },
    confirmingSubscription: {
      type: Boolean,
      default: false,
    },
    creditCard: {
      type: Object as () => { brand: string; last4: string; expiryMonth: string; expiryYear: string },
      default: null,
    },
    selectedTeamId: {
      type: Number,
      required: true,
    },
    paymentError: {
      type: String,
      default: "",
    },
    couponCode: {
      type: String,
      default: "",
    },
  },
  emits: ["onClose", "confirmSubscription", "resetPaymentError", "update:couponCode"],
  computed: {
    getPlanPrice() {
      return `$${this.isYearlySelected ? this.plan.yearlyPrice : this.plan.monthlyPrice}`;
    },
  },
  methods: {
    closeModal() {
      this.$emit("onClose");
    },
    confirmSubscription() {
      this.$emit("confirmSubscription");
    },
    resetPaymentError() {
      this.$emit("resetPaymentError");
    },
  },
});
</script>
